import dayjs from 'dayjs';
import { termAmount } from './bookingFormMoney';
import { listedCount } from './inventoryList';
import * as contactsApi from '../api/contacts';
import * as bookingFormsApi from '../api/bookingForms';

// "Fill from an OTP / OTR" on the CID Forms documents that come out of an agreement
// (owner, 2026-10-04): the three Letters of Undertaking and the two Inventory Lists.
// The agent picks one of their own agreements and the document takes what that
// agreement already says — parties, reference, date, money, and the list itself.
// Both the phone DocumentWorkspacePage and the desktop DocumentWorkspace use this.
//
// The keys on the left are the CID Forms fields (cid-agent-server's
// libraryDoc.service DOCS); on the right, the agreement's form_data.

/** Which agreement each document is filled from. */
const SOURCE = {
  'vendor-letter-of-undertaking': 'OTP',
  'purchaser-letter-of-undertaking': 'OTP',
  'landlord-letter-of-undertaking': 'OTR',
  'inventory-list-sale': 'OTP',
  'inventory-list-rental': 'OTR',
};

/** The document's slug — its file name without `.pdf`, as the server keys DOCS. */
export const docSlug = (doc) => String(doc?.file_name || '').replace(/\.pdf$/i, '');

/** 'OTP' | 'OTR' when this document can be filled from an agreement, else null. */
export const agreementTypeFor = (doc) => SOURCE[docSlug(doc)] || null;

export const AGREEMENT_NAME = { OTP: 'Agreement to Purchase', OTR: 'Agreement to Rent' };
export const AGREEMENTS_NAME = { OTP: 'Agreements to Purchase', OTR: 'Agreements to Rent' };

/** The line under the "Fill from" button — what the agreement brings with it. */
export const fillFromHint = (doc) => {
  const type = agreementTypeFor(doc);
  if (!type) return '';
  const list = docSlug(doc).startsWith('inventory-list') ? ', and its Inventory List' : '';
  return `Takes the parties, reference, date and figures from an ${AGREEMENT_NAME[type]} you prepared${list}.`;
};

/**
 * Whether a list row (GET /booking-forms) is offered in the picker. A cancelled
 * agreement is a dead deal; its terms must not reach a letter. The list carries the
 * status as its label, not the raw value.
 */
export const isPickable = (row) => String(row?.status || '').toLowerCase() !== 'cancelled';

/** The picker row's second line: who is on it, its status, its date. */
export const agreementSub = (row) => [
  (row.parties || []).map((p) => p.value).filter(Boolean).join(' · ') || null,
  row.status,
  row.date && dayjs(row.date).isValid() ? dayjs(row.date).format('D MMM YYYY') : null,
].filter(Boolean).join(' — ');

const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const numOf = (v) => {
  if (!has(v)) return undefined;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};
// The agreement stores a date as YYYY-MM-DD; a CID Forms date field holds the
// DD/MM/YYYY string its template prints verbatim (the `today` default is en-GB).
const docDate = (v) => {
  if (!has(v)) return undefined;
  const s = String(v).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const d = dayjs(s.slice(0, 10));
  return d.isValid() ? d.format('DD/MM/YYYY') : undefined;
};
// The letters offer only Cash or Cheque; the agreement's payment method is free
// text ("Bank transfer", "Cash / Cheque"), so anything else is left for the agent.
const paymentType = (v) => {
  const s = String(v || '').trim().toLowerCase();
  return s === 'cash' ? 'Cash' : s === 'cheque' ? 'Cheque' : undefined;
};

/**
 * The CID Forms values one agreement fills in. `form` is the agreement's detail
 * payload (GET /booking-forms/:id: short-code `form_type`, `form_ref`, `form_data`).
 * Only keys with a value come back — a blank on the agreement never wipes what
 * the agent typed — and only keys the document actually has (`fieldKeys`).
 */
export function valuesFromAgreement(slug, form, fieldKeys) {
  const fd = form?.form_data || {};
  const rent = form?.form_type === 'OTR';
  const base = numOf(rent ? fd.rental_rm : fd.selling_price_rm);
  const earnest = termAmount(fd, 'earnest_deposit', rent ? 'RENT' : 'SALES', base || 0);
  const out = {
    property_address: fd.property_address,
    payment_type: paymentType(fd.payment_type),
    // The letter prints "… <payment> no. <cheque no.>": only a cheque has a number
    // that belongs there — an online-transfer reference would read as a cheque.
    cheque_no: paymentType(fd.payment_type) === 'Cheque' ? fd.payment_ref_no : undefined,
    stakeholder: fd.stakeholder,
    earnest_deposit_rm: earnest > 0 ? Math.round(earnest * 100) / 100 : undefined,
  };

  if (slug === 'vendor-letter-of-undertaking' || slug === 'purchaser-letter-of-undertaking') {
    const p = slug === 'vendor-letter-of-undertaking' ? 'vendor' : 'purchaser';
    const src = p === 'vendor' ? 'owner' : 'purchaser';
    Object.assign(out, {
      [`${p}_name`]: fd[`${src}_1_name`],
      [`${p}_nric`]: fd[`${src}_1_nric`],
      [`${p}_2_name`]: fd[`${src}_2_name`],
      [`${p}_2_nric`]: fd[`${src}_2_nric`],
      atp_ref_no: form?.form_ref,
      atp_date: docDate(fd.date),
      purchase_price_rm: base,
    });
    // The vendor's fee is the one the agent set on the agreement's own LOU. Only the
    // figure its basis names is carried — the letter prints just that one.
    if (p === 'vendor') {
      const amount = fd.owner_lou_fee_basis === 'AMOUNT';
      const fee = numOf(amount ? fd.owner_lou_fee_amount_rm : fd.owner_lou_fee_percent);
      if (fee !== undefined) {
        out.fee_basis = amount ? 'AMOUNT' : 'PERCENT';
        out[amount ? 'fee_amount_rm' : 'fee_percent'] = fee;
      }
      out.disbursements_rm = numOf(fd.owner_lou_disbursements_rm);
    }
  }

  if (slug === 'landlord-letter-of-undertaking') {
    Object.assign(out, {
      landlord_name: fd.landlord_1_name,
      landlord_nric: fd.landlord_1_nric,
      landlord_2_name: fd.landlord_2_name,
      landlord_2_nric: fd.landlord_2_nric,
      atr_ref_no: form?.form_ref,
      atr_date: docDate(fd.date),
      monthly_rental_rm: base,
      fee_months: numOf(fd.landlord_lou_fee_months),
      sst_charged: ['CHARGED', 'ABSORBED'].includes(fd.landlord_lou_sst) ? fd.landlord_lou_sst : undefined,
      disbursements_rm: numOf(fd.landlord_lou_disbursements_rm),
    });
  }

  if (slug === 'inventory-list-sale' || slug === 'inventory-list-rental') {
    const [owner, buyer] = rent ? ['landlord', 'tenant'] : ['owner', 'purchaser'];
    Object.assign(out, {
      agreement_ref: form?.form_ref,
      owner_name: fd[`${owner}_1_name`],
      owner_nric: fd[`${owner}_1_nric`],
      second_owner_name: fd[`${owner}_2_name`],
      second_owner_nric: fd[`${owner}_2_nric`],
      buyer_name: fd[`${buyer}_1_name`],
      buyer_nric: fd[`${buyer}_1_nric`],
      second_buyer_name: fd[`${buyer}_2_name`],
      second_buyer_nric: fd[`${buyer}_2_nric`],
      // Only a list the agreement actually carries — a switched-off one may still
      // hold rooms from before it was switched off, and those were never signed.
      inventory: fd.include_inventory_list === true && listedCount(fd.inventory) > 0 ? fd.inventory : undefined,
    });
  }

  const keys = new Set(fieldKeys || []);
  const picked = {};
  for (const [k, v] of Object.entries(out)) {
    if (!keys.has(k)) continue;
    if (k === 'inventory') { if (v) picked[k] = v; continue; }
    if (has(v)) picked[k] = typeof v === 'string' ? v.trim() : v;
  }
  return picked;
}

/**
 * The letter's party address and contact number, from the party's contact record
 * (the agreement keeps only its id). A contact the agent cannot read — someone
 * else's, outside their deals — is a 404 and is simply left for them to type.
 */
const CONTACT_OF = {
  'vendor-letter-of-undertaking': ['vendor', 'owner_1_id', 'vendor'],
  'landlord-letter-of-undertaking': ['landlord', 'landlord_1_id', 'vendor'],
  'purchaser-letter-of-undertaking': ['purchaser', 'purchaser_1_id', 'purchaser'],
};
export async function contactValuesFromAgreement(slug, form, fieldKeys) {
  const [p, idKey, registry] = CONTACT_OF[slug] || [];
  const id = p && form?.form_data?.[idKey];
  if (!id) return {};
  try {
    const r = await (registry === 'vendor' ? contactsApi.getVendorsById(id) : contactsApi.getPurchasersById(id));
    const c = r?.data ?? r ?? {};
    const keys = new Set(fieldKeys || []);
    const out = {};
    if (keys.has(`${p}_address`) && has(c.address)) out[`${p}_address`] = String(c.address).trim();
    if (keys.has(`${p}_contact`) && has(c.phone)) out[`${p}_contact`] = String(c.phone).trim();
    return out;
  } catch {
    return {};
  }
}

/**
 * Load the picked agreement and work out what it fills in. Resolves to
 * { form, patch } — `patch` is to be MERGED over the document's values, never to
 * replace them. Throws when the agreement cannot be read.
 */
export async function fillFromAgreement(slug, agreementId, fieldKeys) {
  const r = await bookingFormsApi.getBookingFormsById(agreementId);
  const form = r?.data ?? r;
  if (!form?.form_data) throw new Error('Could not read that agreement');
  const patch = {
    ...valuesFromAgreement(slug, form, fieldKeys),
    ...(await contactValuesFromAgreement(slug, form, fieldKeys)),
  };
  return { form, patch };
}

/**
 * Whether taking the agreement's list would throw away one the agent has already
 * built here — the one value a fill can lose real work on, so the screens ask.
 */
export const replacesOwnInventory = (current, patch) => !!patch?.inventory
  && listedCount(current) > 0
  && JSON.stringify(current) !== JSON.stringify(patch.inventory);
